/* eslint-disable no-underscore-dangle */
import axios from 'axios';
import find from 'lodash/find';
import noop from 'lodash/noop';
import get from 'lodash/get';
import Player from '../models/Player';
import hlsEvents from '../hlsjs/constants/hlsEvents';
import hlsTags from '../hlsjs/constants/hlsTags';
import { actions } from '../constants/logging';
import debug from '../../debugger';
import logCategoryActionLabel from '../../loggers/logCategoryActionLabel';
import handleInstreamError from '../utils/handleInstreamError';
import playerName from '../constants/playerName';

const INSTREAM_AD_META_END = 'X-TUNEIN-AD-EVENT="END"';
const INSTREAM_AD_META_START = 'X-TUNEIN-AD-EVENT="START"';
const TIME_UNTIL_AUTO_REQUEST = 3000;

export default class InstreamAdPlayer extends Player {
  constructor(hlsjsPlayer, timeout, isInstreamLoggingEnabled, tuner) {
    super(playerName.InstreamAdPlayer);
    this.hlsjsPlayer = hlsjsPlayer;
    this.timeout = timeout;
    this.isInstreamLoggingEnabled = isInstreamLoggingEnabled;
    this.trackingUrl = null;
    this.tuner = tuner;
  }

  on(event, callback) {
    this.hlsjsPlayer.on(event, callback);
    this.addListener(event, callback);
  }

  off(event, callback) {
    this.hlsjsPlayer.off(event, callback);
    this.removeListener(event, callback);
  }

  async load(url, format, position, trackingUrl) {
    try {
      this.trackingUrl = trackingUrl;
      await this.hlsjsPlayer.load(url, format);
    } catch (error) {
      handleInstreamError(
        error,
        actions.masterUrlTimeout,
        actions.masterUrlError,
        this.isInstreamLoggingEnabled,
      );

      throw error;
    }
  }

  play() {
    return this.hlsjsPlayer.play().then(this._onPlayHandler).catch(noop);
  }

  pause() {
    this._removeFragmentTagListener();
    return this.hlsjsPlayer.pause();
  }

  stop() {
    this._removeFragmentTagListener();
    return this.hlsjsPlayer.stop();
  }

  unload() {
    this._removeFragmentTagListener();
    return this.hlsjsPlayer.unload();
  }

  destroy() {
    this._removeFragmentTagListener();
    return this.hlsjsPlayer.destroy();
  }

  getPlaybackRate() {
    return this.hlsjsPlayer.getPlaybackRate();
  }

  canChangePlaybackRate() {
    return this.hlsjsPlayer.canChangePlaybackRate();
  }

  changePlaybackRate(rate) {
    this.hlsjsPlayer.changePlaybackRate(rate);
  }

  setVolume(volume) {
    this.hlsjsPlayer.setVolume(volume);
  }

  toggleMute() {
    this.hlsjsPlayer.toggleMute();
  }

  getVolume() {
    return this.hlsjsPlayer.getVolume();
  }

  setTitle(title) {
    this.hlsjsPlayer.setTitle(title);
  }

  getTimes() {
    return this.hlsjsPlayer.getTimes();
  }

  requestInstreamAds() {
    this._requestTrackingData();
  }

  _onPlayHandler = () => {
    this._removeFragmentTagListener();
    this._requestTrackingData();

    // Since the tracking url is being manually requested here,
    // the timeout is used to ignore the tags to avoid
    // _requestTrackingData being trigged by the tags at the same time
    setTimeout(this._addFragmentTagListener, TIME_UNTIL_AUTO_REQUEST);
  };

  async _requestTrackingData(forceUpdate = false) {
    try {
      const response =
        (await axios.get(this.trackingUrl, {
          timeout: this.timeout,
        })) || {};

      const ads = get(response, 'data.avails[0].ads', []);
      const hasAds = ads.length > 0;
      if (!hasAds && this.isInstreamLoggingEnabled) {
        logCategoryActionLabel({ action: actions.trackingURLEmpty });
      }

      const playerTime = this.getTimes() || {};
      const adMetadata = {
        playerPosition: playerTime.elapsedSeconds,
        forceUpdate,
        ...response.data,
      };

      this.tuner.trigger(this.tuner.events.instreamAd, adMetadata);
    } catch (error) {
      handleInstreamError(
        error,
        actions.trackingURLTimeout,
        actions.trackingURLError,
        this.isInstreamLoggingEnabled,
      );

      debug.log('an error occurred while polling for ads', error);
    }
  }

  _fragmentTagHandler = (eventName, resp) => {
    const hasDateRangeTag = find(
      resp.frag.tagList,
      (tag) => tag[0] === hlsTags.DATERANGE,
    );

    if (!hasDateRangeTag) {
      return;
    }

    const tagMeta = hasDateRangeTag[1];

    if (!tagMeta) {
      return;
    }

    // AD HAS POTENTIALLY BEEN CUT OFF (FORCE UPDATE)
    if (tagMeta.indexOf(INSTREAM_AD_META_END) >= 0) {
      this._requestTrackingData(true);
      return;
    }

    // AD DATA IS AVAILABLE
    if (tagMeta.indexOf(INSTREAM_AD_META_START) >= 0) {
      this._requestTrackingData();
    }
  };

  _removeFragmentTagListener = () => {
    this.hlsjsPlayer.off(hlsEvents.FRAG_LOADED, this._fragmentTagHandler);
  };

  _addFragmentTagListener = () => {
    this.hlsjsPlayer.on(hlsEvents.FRAG_LOADED, this._fragmentTagHandler);
  };

  _parseCookieString = (str, name) => {
    const splitReadyStr = `; ${str}`;
    const parts = splitReadyStr.split('; ' + name + '=');
    return parts.length === 2 ? parts.pop().split(';').shift() : undefined;
  };
}
